import { logAmplitudeEvent } from 'amplitude/amplitude'
import { History } from 'history'
import {
  createTemplateDataForOrg,
  createTemplateFromSystem,
} from 'projectSections/sections/design/sideDrawer/autoDesign/V2/templateCreation'
import { DataProviderProxy } from 'ra-core'
import { Dispatch } from 'redux'
import { handleAutoDesignEphemeral } from './actions/handleAutoDesignEphemeral'
import { handleLoadAutoDesignResult } from './actions/handleLoadAutoDesignResult'
import { handleNavigate } from './actions/handleNavigate'
import { handleRunAutoString } from './actions/handleRunAutoString'
import { handleRunBatteryDesignAssistant } from './actions/handleRunBatteryDesignAssistant'
import { handleRunInverterDesignAssistant } from './actions/handleRunInverterDesignAssistant'
import { handleSaveSystemDesignAsTemplate } from './actions/handleSaveSystemDesignAsTemplate'
import { handleSystemEdit } from './actions/handleSystemEdit'
import { handleUndoRedo } from './actions/handleUndoRedo'
import { validateActionRequirementsOrThrow } from './utils/validateActionRequirements'

export interface AdaAction {
  action: string
  args: any
}

export function createTemplateFromSelectedSystem(name: string, description?: string | null) {
  const system = window.editor?.selectedSystem
  if (!system) {
    throw new Error('No system selected. Please select a system in the design to save as a template.')
  }

  return createTemplateFromSystem(system, name, description || '')
}

export async function saveSystemDesignAsTemplateAction(
  args: { name: string; description?: string | null },
  orgId: number | undefined,
  projectId: number | undefined,
  dataProvider: DataProviderProxy
) {
  const template = createTemplateFromSelectedSystem(args.name, args.description)

  if (!orgId) {
    throw new Error('Organization ID is required to save template')
  }

  const templateData = createTemplateDataForOrg(template, orgId)

  await dataProvider.create('system_templates', { data: templateData }, { meta: { redirectTo: false } })

  logAmplitudeEvent('auto_design_template_saved_to_org', {
    templateId: template.id,
    templateName: template.name,
    orgId: orgId,
    projectId: projectId,
    source: 'ada_chat_action',
  })

  return { success: true }
}

export function executeActions(
  actions: AdaAction[],
  history: History,
  orgId: number | undefined,
  projectId: number | undefined,
  dataProvider: DataProviderProxy,
  dispatch: Dispatch
) {
  if (window.debugAdaActions) {
    console.log('[ADA DEBUG] Actions received:', {
      count: actions.length,
      actions: actions.map((a) => ({ action: a.action, args: a.args })),
      fullActions: actions,
    })
  }
  const processActions = async () => {
    for (let i = 0; i < actions.length; i++) {
      const action = actions[i]
      await processAction(action, history, orgId, projectId, dataProvider, dispatch)
    }
  }
  processActions()
}

type ActionHandlerContext = {
  history: History
  orgId: number | undefined
  projectId: number | undefined
  dataProvider: DataProviderProxy
  dispatch: Dispatch
}

type ActionHandler = (action: AdaAction, context: ActionHandlerContext) => Promise<void> | void

const actionHandlers: Record<string, ActionHandler> = {
  navigate: async (action, { history }) => {
    await handleNavigate(action, history)
  },
  loadAutoDesignResult: async (action) => {
    await handleLoadAutoDesignResult(action)
  },
  autoDesignEphemeral: async (action, { orgId, projectId, dataProvider, dispatch }) => {
    await handleAutoDesignEphemeral(action, orgId, projectId, dataProvider, dispatch)
  },
  saveSystemDesignAsTemplate: async (action, { orgId, projectId, dataProvider }) => {
    await handleSaveSystemDesignAsTemplate(action, orgId, projectId, dataProvider)
  },
  runAutoString: async (action, { projectId, dispatch }) => {
    await handleRunAutoString(action, projectId, dispatch)
  },
  runBatteryDesignAssistant: async (action, { orgId, projectId, dataProvider, dispatch }) => {
    await handleRunBatteryDesignAssistant(action, orgId, projectId, dataProvider, dispatch)
  },
  runInverterDesignAssistant: async (action, { orgId, projectId, dataProvider, dispatch }) => {
    await handleRunInverterDesignAssistant(action, orgId, projectId, dataProvider, dispatch)
  },
  systemEdit: async (action) => {
    await handleSystemEdit(action)
  },
  undoRedo: async (action) => {
    await handleUndoRedo(action)
  },
}

async function processAction(
  action: AdaAction,
  history: History,
  orgId: number | undefined,
  projectId: number | undefined,
  dataProvider: DataProviderProxy,
  dispatch: Dispatch
) {
  if (window.debugAdaActions) {
    console.log('[ADA DEBUG] Processing action:', {
      action: action?.action,
      actionType: typeof action?.action,
      args: action?.args,
      fullAction: action,
      hasAction: 'action' in (action || {}),
      actionKeys: action ? Object.keys(action) : [],
    })
  }

  // Validate action structure
  if (!action || !action.action) {
    console.error('[ADA DEBUG] Invalid action structure:', action)
    throw new Error(`Invalid action: missing 'action' property. Action object: ${JSON.stringify(action)}`)
  }

  const pathname = window.location.pathname
  try {
    validateActionRequirementsOrThrow(action.action, pathname)
  } catch (error: any) {
    console.error('Action validation failed:', error.message)
    // For voice, this error will bubble up and be returned to the voice API
    // For text, this shouldn't happen as UI should prevent invalid actions
    throw error
  }

  const handler = actionHandlers[action.action]
  if (handler) {
    await handler(action, { history, orgId, projectId, dataProvider, dispatch })
  } else {
    console.warn('Unknown action:', action.action, action.args)
  }
}
